/**
 * Sharad Navratri → Dasara, 2026.
 * Ghatasthapana 11 Oct, nine nights through 19 Oct, Vijayadashami 20 Oct.
 * Colour sequence follows the widely published 2026 calendar (Sunday start).
 * Update this file when next year's tithi dates are confirmed.
 */

export const NAVRATRI_DAYS = [
  {
    id: "d1",
    date: "2026-10-11",
    shortDate: "11 Oct",
    weekday: "Sunday",
    day: 1,
    goddess: "Shailaputri",
    colorName: "Orange",
    hex: "#E86A1A",
    ink: "#FFF8F3",
    light: false,
    meaning: "The first night. A new chapter, and a house ready for whoever is arriving.",
  },
  {
    id: "d2",
    date: "2026-10-12",
    shortDate: "12 Oct",
    weekday: "Monday",
    day: 2,
    goddess: "Brahmacharini",
    colorName: "White",
    hex: "#F4EFE4",
    ink: "#2A2418",
    light: true,
    meaning: "A quieter colour. Room to cook, gather, and stay in.",
  },
  {
    id: "d3",
    date: "2026-10-13",
    shortDate: "13 Oct",
    weekday: "Tuesday",
    day: 3,
    goddess: "Chandraghanta",
    colorName: "Red",
    hex: "#C4313A",
    ink: "#FFF6F6",
    light: false,
    meaning: "The city is loud tonight. Your stay does not have to be.",
  },
  {
    id: "d4",
    date: "2026-10-14",
    shortDate: "14 Oct",
    weekday: "Wednesday",
    day: 4,
    goddess: "Kushmanda",
    colorName: "Royal blue",
    hex: "#1B3F8F",
    ink: "#F4F7FF",
    light: false,
    meaning: "Midway through the nine nights. Room for everyone who is coming.",
  },
  {
    id: "d5",
    date: "2026-10-15",
    shortDate: "15 Oct",
    weekday: "Thursday",
    day: 5,
    goddess: "Skandamata",
    colorName: "Yellow",
    hex: "#F0C21E",
    ink: "#2A2204",
    light: true,
    meaning: "The colour of marigolds. A kitchen, a table, a long evening.",
  },
  {
    id: "d6",
    date: "2026-10-16",
    shortDate: "16 Oct",
    weekday: "Friday",
    day: 6,
    goddess: "Katyayani",
    colorName: "Green",
    hex: "#1B7A45",
    ink: "#F4FFF8",
    light: false,
    meaning: "Friday of the festival. Book the weekend before the weekend books you.",
  },
  {
    id: "d7",
    date: "2026-10-17",
    shortDate: "17 Oct",
    weekday: "Saturday",
    day: 7,
    goddess: "Kalaratri",
    colorName: "Grey",
    hex: "#C5C8CE",
    ink: "#1C1E22",
    light: true,
    meaning: "The deep night. A calm house after the garba.",
  },
  {
    id: "d8",
    date: "2026-10-18",
    shortDate: "18 Oct",
    weekday: "Sunday",
    day: 8,
    goddess: "Mahagauri",
    colorName: "Purple",
    hex: "#6A2C84",
    ink: "#FBF6FF",
    light: false,
    meaning: "Almost there. One more night, then Dasara morning.",
  },
  {
    id: "d9",
    date: "2026-10-19",
    shortDate: "19 Oct",
    weekday: "Monday",
    day: 9,
    goddess: "Siddhidatri",
    colorName: "Peacock",
    hex: "#0C7C72",
    ink: "#F3FFFC",
    light: false,
    meaning: "The ninth night. The last dance before the tenth day.",
  },
];

export const DASARA = {
  id: "dasara",
  date: "2026-10-20",
  shortDate: "20 Oct",
  weekday: "Tuesday",
  day: 10,
  goddess: "Vijayadashami",
  colorName: "Dasara",
  hex: "#E8B34A",
  ink: "#2A1208",
  light: true,
  meaning: "The tenth day. Victory, a long lunch, and a home you do not have to leave in a hurry.",
};

export const FESTIVE_NIGHTS = [...NAVRATRI_DAYS, DASARA];

const WINDOW_START = new Date(2026, 9, 10, 0, 0, 0);
const WINDOW_END = new Date(2026, 9, 21, 23, 59, 59);

function parseISODate(iso) {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isNavratriDasaraSeason(now = new Date()) {
  return now >= WINDOW_START && now <= WINDOW_END;
}

export function getFestiveFocus(now = new Date()) {
  const today = startOfLocalDay(now);
  const dasaraDay = parseISODate(DASARA.date);

  if (today >= dasaraDay) {
    return {
      kind: "dasara",
      phase: today.getTime() === dasaraDay.getTime() ? "today" : "after",
      day: DASARA,
    };
  }

  const current = NAVRATRI_DAYS.find(
    (night) => parseISODate(night.date).getTime() === today.getTime()
  );
  if (current) return { kind: "navratri", phase: "today", day: current };

  const upcoming = NAVRATRI_DAYS.find((night) => parseISODate(night.date) > today);
  return { kind: "navratri", phase: "upcoming", day: upcoming || NAVRATRI_DAYS[0] };
}

export function getFestiveHeroCopy(now = new Date()) {
  const focus = getFestiveFocus(now);
  if (focus.kind === "dasara") {
    return {
      eyebrow: "Dasara · Vijayadashami",
      swatch: "#E8B34A",
      lightSwatch: true,
      line:
        focus.phase === "today"
          ? "The tenth day. Stay for the celebration."
          : "Dasara has arrived. Stay somewhere that feels like yours.",
    };
  }

  const { day, phase } = focus;
  return {
    eyebrow:
      phase === "today"
        ? `Navratri · Day ${day.day}`
        : `Navratri begins ${day.shortDate}`,
    swatch: day.hex,
    lightSwatch: day.light,
    line: phase === "today" ? "Stay for the festival." : "Nine nights, then Dasara.",
  };
}
